import { useCallback } from 'react';
import { calculateRect, deepCloneNodes, calculateLayout, measureNode } from '../utils.js';
import { SnapManager } from '../services/SnapManager.js';
import { resolveNode } from '../utils/responsive.js'; // Import resolveNode

const DRAG_THRESHOLD = 3;

export const useNodeDragging = ({
  draggingRef,
  activeNodes,
  selectedNodeIds,
  updateNodes,
  setSnapLines,
  canvasScale,
  canvasRef,
  rootW,
  rootH,
  settings,
  containerNode,
  activeBreakpoint = 'default',
  visualBreakpoint = 'default', // Add visualBreakpoint
  setSelectedNodeIds,
  collapseNode
}) => {
  const startDrag = useCallback((e, nodeIds, initialRects, axis = null) => {
    draggingRef.current = {
      initialRects,
      startX: e.clientX,
      startY: e.clientY,
      hasMoved: false,
      axis
    };
  }, [draggingRef]);

  const updateDrag = useCallback((e) => {
    if (!draggingRef.current) return false;
    
    // Use visualBreakpoint for all calculations to match what the user sees
    const effectiveVisualBreakpoint = visualBreakpoint || activeBreakpoint;

    const { startX, startY, initialRects, hasMoved, axis } = draggingRef.current;

    // Check threshold
    if (!hasMoved) {
      const distance = Math.sqrt(Math.pow(e.clientX - startX, 2) + Math.pow(e.clientY - startY, 2));
      if (distance < DRAG_THRESHOLD) return false;

      draggingRef.current.hasMoved = true;

      // Alt-drag to duplicate
      if (e.altKey) {
        const dragRoots = activeNodes.filter(n =>
          initialRects.has(n.id) && !initialRects.has(n.parentId || '')
        );

        if (dragRoots.length > 0) {
          const allClones = [];
          const newRects = new Map();
          const newSelection = [];

          dragRoots.forEach(root => {
            const res = deepCloneNodes(activeNodes, root.id);
            if (res) {
              allClones.push(...res.newNodes);
              if (collapseNode && res.newRootId) {
                collapseNode(res.newRootId);
              }
              res.idMap.forEach((newId, oldId) => {
                if (initialRects.has(oldId)) {
                  newRects.set(newId, initialRects.get(oldId));
                }
                if (selectedNodeIds.includes(oldId)) {
                  newSelection.push(newId);
                }
              });
            }
          });

          draggingRef.current.initialRects = newRects;
          updateNodes([...activeNodes, ...allClones]);
          // Update selection to cloned nodes
          if (setSelectedNodeIds && newSelection.length > 0) {
            setSelectedNodeIds(newSelection);
          }
          return true;
        }
      }
    }

    let deltaX = (e.clientX - startX) / canvasScale;
    let deltaY = (e.clientY - startY) / canvasScale;

    if (axis === 'x') deltaY = 0;
    if (axis === 'y') deltaX = 0;

    // Apply snapping
    // Calculate combined bounding box of all dragged nodes
    let minVisualX = Infinity, minVisualY = Infinity;
    let maxVisualX = -Infinity, maxVisualY = -Infinity;
    let hasRects = false;

    // Determine parent context dimensions (Padding Box)
    let pW = rootW;
    let pH = rootH;
    
    if (containerNode) {
         const cRect = measureNode(containerNode.id, activeNodes, rootW, rootH, effectiveVisualBreakpoint);
         const { offsetX, offsetY } = SnapManager.getGridOffset(containerNode);
         pW = cRect.width - (offsetX * 2);
         pH = cRect.height - (offsetY * 2);
    }

    initialRects.forEach((rect, id) => {
        if (rect) {
            hasRects = true;
            
            // Get node to access anchors
            const node = activeNodes.find(n => n.id === id);
            if (!node) return;

            // Use resolved node for anchors
            const resolved = resolveNode(node, effectiveVisualBreakpoint);
            
            // Calculate Visual Rect (relative to parent padding box)
            // Use MEASURED dimensions for width/height to be accurate (captures rendered size)
            const measured = measureNode(id, activeNodes, rootW, rootH, effectiveVisualBreakpoint);
            
            // Calculate Visual Top-Left based on anchors + prop X/Y
            // rect.x/y are the initial props captured at startDrag
            const pixelLeft = (pW * (resolved.anchors?.minX || 0)) + rect.x;
            const pixelTop = (pH * (resolved.anchors?.minY || 0)) + rect.y;
            
            // Use measured pixel dimensions
            const pixelW = measured.width;
            const pixelH = measured.height;
            
            minVisualX = Math.min(minVisualX, pixelLeft);
            minVisualY = Math.min(minVisualY, pixelTop);
            maxVisualX = Math.max(maxVisualX, pixelLeft + pixelW);
            maxVisualY = Math.max(maxVisualY, pixelTop + pixelH);
        }
    });

    if (hasRects) {
      const combinedWidth = maxVisualX - minVisualX;
      const combinedHeight = maxVisualY - minVisualY;
      
      // Target is Visual Local Left + Delta
      let targetX = minVisualX + deltaX;
      let targetY = minVisualY + deltaY;

      const snapResult = SnapManager.applySnapToPosition(
        targetX,
        targetY,
        combinedWidth,
        combinedHeight,
        settings,
        selectedNodeIds,
        activeNodes,
        rootW,
        rootH,
        containerNode,
        axis,
        effectiveVisualBreakpoint
      );

      // Snap Result returns new Visual Local Top-Left
      // Calculate new delta based on how much we snapped relative to start
      const snappedDeltaX = snapResult.x - minVisualX;
      const snappedDeltaY = snapResult.y - minVisualY;
      
      setSnapLines(snapResult.snapLines);

      deltaX = snappedDeltaX;
      deltaY = snappedDeltaY;

      if (axis === 'x') deltaY = 0;
      if (axis === 'y') deltaX = 0;
    }

    // Apply drag
    const newNodes = activeNodes.map(n => {
      if (!initialRects.has(n.id)) return n;

      // Skip if parent is also being dragged
      let p = n.parentId;
      while (p) {
        if (initialRects.has(p)) return n;
        p = activeNodes.find(x => x.id === p)?.parentId;
      }

      // Calculate parent dimensions
      let pW = rootW;
      let pH = rootH;
      // We must resolve parent to get correct dimensions for the current breakpoint
      const rawParent = activeNodes.find(p => p.id === n.parentId);
      if (rawParent) {
        const resolvedParent = resolveNode(rawParent, effectiveVisualBreakpoint);
        const pRect = calculateRect(resolvedParent, rootW, rootH);
        pW = pRect.width;
        pH = pRect.height;
      }

      // Get initial rect and apply delta
      const initialRect = initialRects.get(n.id);
      const newPixelRect = {
        ...initialRect,
        x: Math.round(initialRect.x + deltaX),
        y: Math.round(initialRect.y + deltaY)
      };

      // Convert back to layout using the node's anchors
      // We use the RESOLVED anchors (from visual state) to calculate the new X/Y.
      const resolvedNode = resolveNode(n, effectiveVisualBreakpoint);
      const newLayout = calculateLayout(newPixelRect, resolvedNode.anchors, pW, pH);
      
      const finalX = Math.round(newLayout.x);
      const finalY = Math.round(newLayout.y);

      // Write using activeBreakpoint (the one selected for editing)
      if (activeBreakpoint === 'default') {
          return {
              ...n,
              x: finalX,
              y: finalY
          };
      } else {
          // Write to responsiveOverrides
          const currentOverrides = n.props.responsiveOverrides || {};
          const currentBpOverrides = currentOverrides[activeBreakpoint] || {};
          
          return {
              ...n,
              props: {
                  ...n.props,
                  responsiveOverrides: {
                      ...currentOverrides,
                      [activeBreakpoint]: {
                          ...currentBpOverrides,
                          layout_x: finalX,
                          layout_y: finalY
                      }
                  }
              }
          };
      }
    });

    updateNodes(newNodes);
    return true;
  }, [
    draggingRef,
    activeNodes,
    selectedNodeIds,
    updateNodes,
    setSnapLines,
    canvasScale,
    canvasRef,
    rootW,
    rootH,
    settings,
    containerNode,
    activeBreakpoint,
    visualBreakpoint
  ]);

  const stopDrag = useCallback(() => {
    const hasMoved = draggingRef.current?.hasMoved || false;
    draggingRef.current = null;
    setSnapLines([]);
    return hasMoved;
  }, [draggingRef, setSnapLines]);

  return { startDrag, updateDrag, stopDrag };
};
